import axios from 'axios';
import { 
  Story, 
  StoryCreateRequest, 
  StoryUpdateRequest, 
  StoryComment,
  User, 
  SearchFilters, 
  PaginatedResponse,
  SearchStoriesResponse 
} from '../types';

export const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:8081/api',
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 300000, // 5 minutes timeout for AI operations (story improvement can take 3-5 minutes)
});

// Request interceptor to add auth token
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      // Clear expired/invalid token from storage
      localStorage.removeItem('token');

      // Check if this is a public endpoint
      const publicEndpoints = ['/stories/public', '/stories/genres', '/stories/featured', '/stories/dashboard'];
      const isPublicEndpoint = publicEndpoints.some(endpoint => error.config?.url?.includes(endpoint));

      if (isPublicEndpoint && !error.config._retry) {
        // For public endpoints, retry without the Authorization header
        error.config._retry = true;
        delete error.config.headers.Authorization;
        return apiClient.request(error.config);
      } else if (!isPublicEndpoint) {
        // For protected endpoints, redirect to login
        window.location.href = '/';
      }
    }
    return Promise.reject(error);
  }
);

// Story API
export const storyAPI = {
  getTopPublicStories: (limit = 15) =>
    apiClient.get<Story[]>(`/stories/public/top?limit=${limit}`),
  
  getFeaturedStories: () =>
    apiClient.get<Story[]>('/stories/featured'),
  
  searchPublicStories: (params: SearchFilters) => {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        // Map 'query' to 'q' for backend compatibility
        const paramKey = key === 'query' ? 'q' : key;
        searchParams.append(paramKey, value.toString());
      }
    });
    return apiClient.get<SearchStoriesResponse>(`/stories/public/search?${searchParams.toString()}`);
  },
  
  getGenres: () =>
    apiClient.get<string[]>('/stories/genres'),
  
  getMyStories: (genre?: string) =>
    apiClient.get<Story[]>(`/stories/my${genre ? `?genre=${genre}` : ''}`),
  
  getAccessibleStoriesByAuthor: (authorId: string) =>
    apiClient.get<Story[]>(`/stories/accessible/${authorId}`),
  
  getStory: (id: string) =>
    apiClient.get<Story>(`/stories/${id}`),
  
  getPublicStory: (id: string) =>
    apiClient.get<Story>(`/stories/${id}`),
  
  createStory: (story: StoryCreateRequest) => 
    apiClient.post<Story>('/stories', story),
  
  updateStory: (id: string, story: StoryUpdateRequest) => 
    apiClient.put<Story>(`/stories/${id}`, story),
  
  deleteStory: (id: string) =>
    apiClient.delete(`/stories/${id}`),
  
  rateStory: (id: string, rating: number, comment?: string) =>
    apiClient.post<Story>(`/stories/${id}/rate`, { rating, comment }),
  
  getMyRating: (id: string) =>
    apiClient.get<{rating: number, comment?: string}>(`/stories/${id}/my-rating`),
    
  addComment: (id: string, content: string) =>
    apiClient.post<StoryComment>(`/stories/${id}/comments`, { content }),
    
  getComments: (id: string) =>
    apiClient.get<StoryComment[]>(`/stories/${id}/comments`),
    
  getPublicComments: (id: string) =>
    apiClient.get<StoryComment[]>(`/stories/public/${id}/comments`),
};

// User API
export const userAPI = {
  getCurrentUser: () =>
    apiClient.get<User>('/users/me'),
  
  syncUser: () =>
    apiClient.post<User>('/users/sync'),
  
  searchUsers: (search: string, page = 0, size = 20) =>
    apiClient.get<PaginatedResponse<User>>(`/users/search?q=${search}&page=${page}&size=${size}`),
  
  updateProfile: (profile: {
    firstName?: string;
    lastName?: string;
    bio?: string;
  }) => apiClient.put<User>('/users/profile', profile),
  
  updateAvatar: (avatarUrl: string) =>
    apiClient.put<User>('/users/avatar', { avatarUrl }),
};

// Friends API (placeholder for future implementation)
export const friendsAPI = {
  getFriends: () =>
    apiClient.get('/friends'),
  
  getPendingRequests: () =>
    apiClient.get('/friends/requests/pending'),
  
  getSentRequests: () =>
    apiClient.get('/friends/requests/sent'),
  
  sendFriendRequest: (userId: string) =>
    apiClient.post(`/friends/request/${userId}`),
  
  acceptFriendRequest: (requestId: string) =>
    apiClient.post(`/friends/accept/${requestId}`),
  
  rejectFriendRequest: (requestId: string) =>
    apiClient.post(`/friends/reject/${requestId}`),
  
  removeFriend: (friendId: string) =>
    apiClient.delete(`/friends/${friendId}`),
  
  createFriendshipLink: (maxUses = 1, expiresInHours = 24) =>
    apiClient.post('/friendship/link', { maxUses, expiresInHours }),
  
  useFriendshipLink: (token: string) =>
    apiClient.post(`/friendship/link/${token}/use`),
};

export default apiClient;